import type { ClientQuestion, ClassScore } from '@mission.io/question-toolkit'

import React from 'react'
import { variantTypes } from '@mission.io/question-toolkit'
import type { QuestionScoreMap, Score } from './types'
import { QuestionGrid } from './QuestionGrid'
import { Header, HeaderCard } from './SharedComponents'
import { convertToMarkdownString } from '@mission.io/question-views'
import type {
	QuestionInfo,
	ClassStudentAnalytics,
	AnalyticsQuestionResponse,
} from '@mission.io/mission-toolkit'

/**
 * CulminatingMomentReview - a react component which shows how the students voted during culminating moments
 *
 * Any updates made to this component CANNOT include useContext
 * @returns React$Node
 */
export function CulminatingMomentReview({
	culminatingMoments,
	analytics,
}: {
	culminatingMoments: Array<{
		questionInfo: QuestionInfo,
		id: string,
		responses: { [studentId: string]: string[] },
	}>,
	analytics: ?ClassStudentAnalytics,
}): React$Node {
	const questions: ClientQuestion[] = []
	const scores: QuestionScoreMap = {}
	const questionClassScores: { [culminatingMomentId: string]: ClassScore } = {}

	if (analytics) {
		analytics?.culminatingMomentQuestions?.forEach(
			culminatingMomentQuestion => {
				questions.push({
					...culminatingMomentQuestion,
					_id: culminatingMomentQuestion.id,
					phrase: convertToMarkdownString(culminatingMomentQuestion.phrase),
					type: variantTypes.MULTIPLE_CHOICE,
					choices: culminatingMomentQuestion.choices.map(choice => ({
						...choice,
						_id: choice.id,
						answer: choice.correct,
						text: convertToMarkdownString(choice.text),
					})),
					default: true,
				})
			}
		)

		const studentAnswers: {
			[culminatingMomentId: string]: {
				[studentId: string]: AnalyticsQuestionResponse[],
			},
		} = {}

		Object.keys(analytics?.studentAnalytics ?? {}).forEach(studentId => {
			const studentAnalytics = analytics?.studentAnalytics?.[studentId]
			if (!studentAnalytics) {
				return
			}
			studentAnalytics?.culminatingMomentAnswers?.forEach(answer => {
				studentAnswers[answer.questionId] ??= {}
				studentAnswers[answer.questionId][studentId] ??= []
				studentAnswers[answer.questionId][studentId].push(answer)
			})
		})

		questions.forEach(question => {
			const questionId = question._id
			const optionCounts: { [optionId: string]: number } = {}

			let answerCount = 0
			let outOf = 0
			let score = 0

			if (!studentAnswers[questionId]) {
				return
			}

			Object.keys(studentAnswers[questionId]).forEach(studentId => {
				studentAnswers[questionId][studentId].sort(
					(a, b) => (a.answerNumber ?? Infinity) - (b.answerNumber ?? Infinity)
				)

				// only count first response
				const firstResponse = studentAnswers[questionId][studentId][0]
				const firstResponseScore = firstResponse?.score
				if (firstResponseScore?.score != null) {
					outOf += firstResponseScore.perfectScore
					score += firstResponseScore.score
				}
				const firstResponseOption = firstResponse?.answer?.value
				if (typeof firstResponseOption !== 'string') {
					return
				}
				optionCounts[firstResponseOption] ??= 0
				optionCounts[firstResponseOption] += 1
				answerCount += 1
			})

			if (outOf !== 0) {
				scores[questionId] = {
					correct: score,
					outOf,
				}
			}

			if (answerCount !== 0) {
				questionClassScores[questionId] = {
					type: variantTypes.MULTIPLE_CHOICE,
					optionScores: Object.keys(optionCounts).map(optionId => ({
						_id: optionId,
						score: (optionCounts[optionId] || 0) / (answerCount || 1),
					})),
				}
			}
		})
	} else if (culminatingMoments) {
		culminatingMoments.forEach(culminatingMoment => {
			const culminatingMomentId = culminatingMoment.id
			const normalizedQuestion: ClientQuestion = {
				type: variantTypes.MULTIPLE_CHOICE,
				phrase: convertToMarkdownString(culminatingMoment.questionInfo.phrase),
				choices: culminatingMoment.questionInfo.options.map(option => ({
					_id: option._id,
					text: convertToMarkdownString(option.text),
					answer: option.correct,
					...(option.imageUrl
						? { media: [{ type: 'IMAGE', url: option.imageUrl }] }
						: {}),
				})),
				_id: culminatingMomentId,
				// $FlowIgnore[incompatible-exact] question-views and mission-toolkit don't both use exact types here. This should be resolved when switching to TS
				media: culminatingMoment.questionInfo.media,
				default: true,
				version: 1,
			}

			const correctAnswers: Set<string> = new Set(
				culminatingMoment.questionInfo.options
					.filter(({ correct }) => correct)
					.map(({ _id }) => _id)
			)
			const score: Score = { correct: 0, outOf: 0 }
			const responses = culminatingMoment.responses
			const optionCount: { [optionId: string]: number } = {}

			Object.keys(responses).forEach(studentId => {
				const studentResponse = responses[studentId]
				if (!studentResponse.length) {
					return
				}

				// base it off of the students first vote
				const pickedOption = studentResponse[0]

				score.correct += Number(correctAnswers.has(pickedOption))
				score.outOf += 1

				optionCount[pickedOption] = (optionCount[pickedOption] || 0) + 1
			})

			if (!score.outOf) {
				return
			}

			questions.push(normalizedQuestion)
			scores[culminatingMomentId] = score
			questionClassScores[culminatingMomentId] = {
				type: variantTypes.MULTIPLE_CHOICE,
				optionScores: Object.keys(optionCount).map(optionId => ({
					_id: optionId,
					score: (optionCount[optionId] || 0) / (score.outOf || 1),
				})),
			}
		})
	}

	if (!questions.length) {
		return null
	}

	return (
		<div>
			<Header>
				<HeaderCard>
					<h3 className="font-game-headline">Application</h3>
				</HeaderCard>
			</Header>
			<QuestionGrid
				questionScores={scores}
				questions={questions}
				questionClassScores={questionClassScores}
			/>
		</div>
	)
}
